import React, { useEffect, useState } from "react";
import { Button } from "@glamcorner/components/lib/core/components/Button";
import { StyleDesignations } from "../../../../common/interfaces";
import { RentalDesignationPanel } from "../../components/DesignationPanels/RentalDesignationPanel";
import { BuyDesignationPanel } from "../../components/DesignationPanels/BuyDesignationPanel";
import { SubscriptionDesignationPanel } from "../../components/DesignationPanels/SubscriptionDesignationPanel";
import { OutOfStock } from "../../components/OutOfStock";
import { useDesignations } from "../../context/DesignationProvider";
import { useSession } from "../../context/SessionProvider";
import { useStyle } from "../../context/StyleProvider";
import { SubscriptionService } from "../../../services/SubscriptionService";
import { BACKUP_PRICE_DOLLARS } from "../../../constants";
import { useProfile } from "../../context/ProfileProvider";
import "./designation-panels-container.scss";

export const DesignationPanelsContainer: React.FC = () => {
  const { gcSession } = useSession();
  const { buyInformation, rentalInformation, subscriptionInformation, isAlwaysNew } = useStyle();
  const { allAvailableDesignations, designation, errors, isValid, productCode, setDesignation, size } =
    useDesignations();
  const { profile } = useProfile();

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [buttonText, setButtonText] = useState("Add to Bag");

  // Trigger each component to run their own validations and add item to box
  let rentalSubmit: () => boolean;
  let buySubmit: () => boolean;
  let subscriptionSubmit: () => boolean;

  const hasRental = allAvailableDesignations.includes(StyleDesignations.RENTAL);
  const hasBuy = allAvailableDesignations.includes(StyleDesignations.BUY);
  const hasSubscription = allAvailableDesignations.includes(StyleDesignations.SUBSCRIPTION);
  const isOutOfStock = allAvailableDesignations.length === 0;

  useEffect(() => {
    setHasSubmitted(false);
  }, [designation, productCode]);

  const shouldShowBuyButton = () => {
    if (designation === StyleDesignations.SUBSCRIPTION) {
      return SubscriptionService.isSubscriptionCustomer(gcSession);
    }

    if (isOutOfStock) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (designation === StyleDesignations.SUBSCRIPTION) {
      setButtonText(hasSubmitted ? "Added to Box" : "Add to Box");
      // Server side render, check if GC is defined

      if (profile?.customerSubscription?.reservedItemsFeatureFlag) {
        setButtonText(
          // @ts-ignore - this is done in a hopes that it's a temporary tag. If permanent, will need to refactor
          <>
            {hasSubmitted ? "Successfully Reserved" : "Reserve this item"}{" "}
            <span className="subscription-reserve-beta-tag">BETA</span>
          </>
        );
      }
    } else if (designation === StyleDesignations.RENTAL && typeof GC !== "undefined" && GC.backupDress?.isEnabled()) {
      setButtonText(`Reserve as a backup ($${BACKUP_PRICE_DOLLARS})`);
    } else {
      setButtonText(hasSubmitted ? "Added to Bag" : "Add to Bag");
    }
  }, [designation, hasSubmitted, profile, gcSession]);

  return (
    <div className="c-designation-panels-container">
      {hasRental && (
        <RentalDesignationPanel
          errors={errors}
          information={rentalInformation!}
          isSelected={designation === StyleDesignations.RENTAL}
          isValid={isValid}
          onSelect={() => setDesignation(StyleDesignations.RENTAL)}
          parentOnSubmitCallback={(fn) => (rentalSubmit = fn)}
          productCode={productCode}
          size={size}
        />
      )}

      {hasBuy && (
        <BuyDesignationPanel
          errors={errors}
          information={buyInformation!}
          isAlwaysNew={isAlwaysNew}
          isSelected={designation === StyleDesignations.BUY}
          isValid={isValid}
          onSelect={() => setDesignation(StyleDesignations.BUY)}
          parentOnSubmitCallback={(fn) => (buySubmit = fn)}
          productCode={productCode}
          size={size}
        />
      )}

      {hasSubscription && (
        <SubscriptionDesignationPanel
          errors={errors}
          information={subscriptionInformation!}
          isValid={isValid}
          productCode={productCode}
          parentOnSubmitCallback={(fn) => (subscriptionSubmit = fn)}
          isSelected={designation === StyleDesignations.SUBSCRIPTION}
          onSelect={() => setDesignation(StyleDesignations.SUBSCRIPTION)}
          size={size}
        />
      )}

      {isOutOfStock && <OutOfStock />}

      {shouldShowBuyButton() && (
        <Button
          text={buttonText}
          onClick={() => {
            switch (designation) {
              case StyleDesignations.SUBSCRIPTION:
                setHasSubmitted(subscriptionSubmit());
                break;
              case StyleDesignations.RENTAL:
                setHasSubmitted(rentalSubmit());
                break;
              case StyleDesignations.BUY:
                setHasSubmitted(buySubmit());
                break;
            }
          }}
        />
      )}
    </div>
  );
};
